import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useLoginStatus } from "../../../../context/LoginStatusContext";
import { apiRequest } from "../../../../utils/api";

interface UsageData {
  member: {
    id: number;
    username: string;
    email: string;
  };
  total_credits: number;
  actions: {
    [key: string]: number;
  };
}

interface OrganizationInfo {
  credit_pool: number;
  contract_start_date: string | null;
  contract_end_date: string | null;
}

interface UsageHistory {
  id: number;
  user: {
    id: number;
    username: string;
    email: string;
  };
  action_name: string;
  page_name: string;
  credits: number;
  created_at: string;
  refunded: boolean;
}

// 展開アイコンのスタイル付きコンポーネント
interface ExpandMoreProps {
  expand: boolean;
}

// ESLintエラーを抑制するためコメントを追加
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ExpandMore = styled((props: { expand: boolean } & React.ComponentProps<typeof IconButton>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { expand, ...other } = props;
  return <IconButton {...other} />;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
})<ExpandMoreProps>(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  backgroundColor: expand ? "rgba(0, 0, 0, 0.04)" : "transparent",
}));

const UsageReport = () => {
  const { t } = useTranslation();
  const { user } = useLoginStatus();
  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [allMembers, setAllMembers] = useState<UsageData[]>([]); // 全メンバーリストを保持する状態
  const [usageHistory, setUsageHistory] = useState<UsageHistory[]>([]);
  const [totalUsage, setTotalUsage] = useState(0);
  const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMember, setSelectedMember] = useState<number | "all">("all");

  // カードの展開状態を管理
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [memberExpanded, setMemberExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(true);

  // 展開状態を切り替える関数
  const handleExpandClick = (section: "summary" | "member" | "history") => {
    if (section === "summary") {
      setSummaryExpanded(!summaryExpanded);
    } else if (section === "member") {
      setMemberExpanded(!memberExpanded);
    } else if (section === "history") {
      setHistoryExpanded(!historyExpanded);
    }
  };

  // 履歴ページネーション用の状態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10; // 固定値として定義

  // メンバー別ページネーション用の状態
  const [memberCurrentPage, setMemberCurrentPage] = useState(1);
  const [memberTotalPages, setMemberTotalPages] = useState(1);
  const memberPageSize = 10; // 固定値として定義

  const organizationUuid = user?.enterprise?.organization?.uuid;
  const userRole = user?.enterprise?.role;
  const permissions = user?.enterprise?.permissions || [];
  const userId = user?.uuid;

  const isOwnerOrManager = userRole === "owner" || userRole === "manager";
  const canViewAllUsage = isOwnerOrManager || permissions.includes("view_all_usage");

  useEffect(() => {
    if (organizationUuid) {
      fetchUsageData();
    }
  }, [organizationUuid, period, selectedMember, currentPage, pageSize, memberCurrentPage, memberPageSize]);

  // 期間が変更されたときに全メンバーリストも更新
  useEffect(() => {
    if (organizationUuid && canViewAllUsage) {
      fetchAllMembers();
    }
  }, [organizationUuid, period]);

  // ページネーション切り替え時に既存データを維持する（削除）

  // 全メンバーデータを取得する関数
  const fetchAllMembers = async () => {
    if (!canViewAllUsage) return; // 権限がない場合は何もしない

    try {
      // "all"フィルタを使用して全メンバーのサマリーを取得
      const allMembersParams = new URLSearchParams();
      allMembersParams.append("period", period);
      allMembersParams.append("page_size", "100"); // 十分な数のメンバーを取得

      const allMembersResponse = await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/usage/summary/?${allMembersParams.toString()}`,
        "GET"
      );

      // すべてのメンバーを保存
      if (allMembersResponse.data && allMembersResponse.data.members) {
        setAllMembers(allMembersResponse.data.members);
      }
    } catch (error) {
      console.error("全メンバーデータ取得エラー:", error);
    }
  };

  const fetchUsageData = async () => {
    try {
      setError("");
      // ローディング状態は更新するが、既存のデータはクリアしない
      // これにより、データ読み込み中もテーブルの高さが維持される

      // 初回ロード時に全メンバーリストも取得
      if (canViewAllUsage && allMembers.length === 0) {
        fetchAllMembers();
      }

      // 期間パラメータの準備
      const params = new URLSearchParams();
      params.append("period", period);

      // ユーザーフィルタリング
      if (selectedMember !== "all" && selectedMember !== null) {
        params.append("user_id", selectedMember.toString());
      }

      // メンバー別データのページネーションパラメータを設定
      const memberParams = new URLSearchParams(params);
      memberParams.append("page", memberCurrentPage.toString());
      memberParams.append("page_size", memberPageSize.toString());

      // 組織の使用状況データを取得（メンバー別データはページネーション対応）
      const usageDataResponse = await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/usage/summary/?${memberParams.toString()}`,
        "GET"
      );

      // 使用履歴ページネーションパラメータを設定
      const historyParams = new URLSearchParams(params);
      historyParams.append("page", currentPage.toString());
      historyParams.append("page_size", pageSize.toString());

      // 使用履歴を取得（ページネーション対応）
      const usageHistoryResponse = await apiRequest(
        `/api/v1/enterprise/organizations/${organizationUuid}/usage/history/?${historyParams.toString()}`,
        "GET"
      );

      // メンバーデータとページネーション情報を設定
      setUsageData(
        canViewAllUsage
          ? usageDataResponse.data.members
          : usageDataResponse.data.members.filter((m) => m.member.id === userId)
      );

      // メンバーのページネーション情報を設定
      if (usageDataResponse.data.members_pagination) {
        setMemberTotalPages(usageDataResponse.data.members_pagination.total_pages || 1);
        // ページ番号を設定（APIからの値がない場合はフロントエンドの状態を維持）
        if (usageDataResponse.data.members_pagination.page) {
          setMemberCurrentPage(parseInt(usageDataResponse.data.members_pagination.page));
        }
      } else {
        // 後方互換性のために古い形式もサポート - ページネーション情報がない場合は1ページとみなす
        setMemberTotalPages(1);
      }

      // ページネーション情報を設定
      if (usageHistoryResponse.data.results) {
        setUsageHistory(
          canViewAllUsage
            ? usageHistoryResponse.data.results
            : usageHistoryResponse.data.results.filter((h) => h.user.id === userId)
        );
        setTotalPages(Math.ceil(usageHistoryResponse.data.count / pageSize));
      } else {
        // 後方互換性のために古い形式もサポート
        setUsageHistory(
          canViewAllUsage ? usageHistoryResponse.data : usageHistoryResponse.data.filter((h) => h.user.id === userId)
        );
      }
      setTotalUsage(usageDataResponse.data.total_credits);

      // 組織情報を設定
      if (usageDataResponse.data.organization_info) {
        setOrganizationInfo(usageDataResponse.data.organization_info);
      }
      setLoading(false);
    } catch (error) {
      console.error(t("preferences.enterprise.error.log.fetchUsageData"), error);
      setError(t("preferences.enterprise.error.fetchUsageData"));
      setLoading(false);
    }
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handleMemberChange = (event) => {
    setSelectedMember(event.target.value);
  };

  if (!user?.enterprise) {
    return <Typography>{t("preferences.enterprise.noEnterpriseInfo")}</Typography>;
  }

  if (loading) {
    return <Typography>{t("preferences.enterprise.loading")}</Typography>;
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* 集計期間選択 */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>{t("preferences.enterprise.period")}</InputLabel>
            <Select value={period} onChange={handlePeriodChange} label={t("preferences.enterprise.period")}>
              <MenuItem value="week">{t("preferences.enterprise.periods.week")}</MenuItem>
              <MenuItem value="month">{t("preferences.enterprise.periods.month")}</MenuItem>
              <MenuItem value="quarter">{t("preferences.enterprise.periods.quarter")}</MenuItem>
              <MenuItem value="year">{t("preferences.enterprise.periods.year")}</MenuItem>
              <MenuItem value="contract">{t("preferences.enterprise.periods.contract")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* ユーザー選択 (管理者のみ) */}
        {canViewAllUsage && (
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3,
            }}
          >
            <FormControl fullWidth>
              <InputLabel>{t("preferences.enterprise.selectedMember")}</InputLabel>
              <Select
                value={selectedMember}
                onChange={handleMemberChange}
                label={t("preferences.enterprise.selectedMember")}
              >
                <MenuItem value="all">{t("preferences.enterprise.allMembers")}</MenuItem>
                {allMembers.length > 0
                  ? allMembers.map((item) => (
                      <MenuItem key={item.member.id} value={item.member.id}>
                        {item.member.username}
                      </MenuItem>
                    ))
                  : usageData.map((item) => (
                      <MenuItem key={item.member.id} value={item.member.id}>
                        {item.member.username}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
      {/* 使用状況サマリー */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title={
            <Typography variant="subtitle1" color="primary" gutterBottom>
              {t("preferences.enterprise.summary")}
            </Typography>
          }
          action={
            <ExpandMore
              expand={summaryExpanded}
              onClick={() => handleExpandClick("summary")}
              aria-expanded={summaryExpanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          }
        />
        <Collapse in={summaryExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <TableContainer component={"div"} sx={{ mt: 1 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>{t("preferences.enterprise.totalCreditsUsed")}</TableCell>
                    <TableCell align="right">
                      <Typography>{totalUsage.toFixed(1)}</Typography>
                    </TableCell>
                  </TableRow>

                  {organizationInfo && (
                    <>
                      <TableRow>
                        <TableCell>{t("preferences.enterprise.creditPool")}</TableCell>
                        <TableCell align="right">
                          <Typography>{organizationInfo.credit_pool.toFixed(1)}</Typography>
                        </TableCell>
                      </TableRow>

                      {organizationInfo.contract_start_date && organizationInfo.contract_end_date && (
                        <TableRow>
                          <TableCell>{t("preferences.enterprise.contractPeriod")}</TableCell>
                          <TableCell align="right">
                            {t("preferences.enterprise.fromTo", {
                              from: organizationInfo.contract_start_date,
                              to: organizationInfo.contract_end_date,
                            })}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Collapse>
      </Card>
      {/* メンバー別使用状況 */}
      {(canViewAllUsage || usageData.length > 0) && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title={
              <Typography variant="subtitle1" color="primary" gutterBottom>
                {t("preferences.enterprise.memberUsage")}
              </Typography>
            }
            action={
              <ExpandMore
                expand={memberExpanded}
                onClick={() => handleExpandClick("member")}
                aria-expanded={memberExpanded}
                aria-label="show more"
              >
                <ExpandMoreIcon />
              </ExpandMore>
            }
          />
          <Collapse in={memberExpanded} timeout="auto" unmountOnExit>
            <CardContent>
              <TableContainer component={"div"} sx={{ mt: 1, minHeight: memberExpanded ? "530px" : 0 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("preferences.enterprise.member.name")}</TableCell>
                      <TableCell>{t("preferences.enterprise.member.email")}</TableCell>
                      <TableCell align="right">{t("preferences.enterprise.totalCredits")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageData.map((item) => (
                      <TableRow key={item.member.id}>
                        <TableCell>{item.member.username}</TableCell>
                        <TableCell>{item.member.email}</TableCell>
                        <TableCell align="right">{item.total_credits.toFixed(1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* メンバー別ページネーションコントロール */}
              {memberTotalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={memberTotalPages}
                    page={memberCurrentPage}
                    onChange={(event, value) => setMemberCurrentPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>
      )}
      {/* 使用履歴詳細 */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title={
            <Typography variant="subtitle1" color="primary" gutterBottom>
              {t("preferences.enterprise.usageHistory")}
            </Typography>
          }
          action={
            <ExpandMore
              expand={historyExpanded}
              onClick={() => handleExpandClick("history")}
              aria-expanded={historyExpanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          }
        />
        <Collapse in={historyExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <TableContainer component={"div"} sx={{ mt: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("preferences.enterprise.dateTime")}</TableCell>
                    {canViewAllUsage && <TableCell>{t("preferences.enterprise.member.name")}</TableCell>}
                    <TableCell>{t("preferences.enterprise.page")}</TableCell>
                    <TableCell>{t("preferences.enterprise.action")}</TableCell>
                    <TableCell align="right">{t("preferences.enterprise.credits")}</TableCell>
                    <TableCell>{t("preferences.enterprise.status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageHistory.map((history) => (
                    <TableRow key={history.id}>
                      <TableCell>{formatDateTime(history.created_at)}</TableCell>
                      {canViewAllUsage && <TableCell>{history.user.username}</TableCell>}
                      <TableCell>{history.page_name}</TableCell>
                      <TableCell>{history.action_name}</TableCell>
                      <TableCell align="right">{history.credits}</TableCell>
                      <TableCell>
                        {history.refunded ? (
                          <Typography color="error">{t("preferences.enterprise.refunded")}</Typography>
                        ) : (
                          <Typography color="success.main">{t("preferences.enterprise.charged")}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ページネーションコントロール */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, value) => setCurrentPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
};

export default UsageReport;
